import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./TreasuryPage.css";

type TreasuryAccount = {
  id: string;
  code: string;
  name: string;
  account_type: "cash" | "bank";
  currency_code: string;
  bank_name: string | null;
  bank_account_number: string | null;
  accounting_account_id: string;
  current_balance: number | string;
  allow_negative_balance: boolean;
  is_default: boolean;
  status: string;
};

type TreasuryAccountAdmin = TreasuryAccount & {
  organization_id: string;
  bank_account_type: string | null;
  cci: string | null;
  accounting_account_code: string;
  accounting_account_name: string;
  opening_balance: number | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

type Receivable = {
  id: string;
  customer_id: string | null;
  customer_name: string | null;
  currency_code: string;
  original_amount: number | string;
  paid_amount: number | string;
  balance_amount: number | string;
  issue_date: string;
  due_date: string | null;
  status: string;
  accounting_document_id: string | null;
  document_number: string | null;
  sale_id: string | null;
  sale_number: string | null;
};

type Payable = {
  id: string;
  supplier_id: string | null;
  supplier_name: string | null;
  currency_code: string;
  original_amount: number | string;
  paid_amount: number | string;
  balance_amount: number | string;
  issue_date: string;
  due_date: string | null;
  status: string;
  purchase_document_id: string | null;
  document_number: string | null;
  purchase_order_id: string | null;
  purchase_number: string | null;
};

type TreasuryMovement = {
  id: string;
  treasury_account_id: string;
  account_name: string;
  movement_number: number | string;
  movement_type: string;
  movement_date: string;
  amount: number | string;
  currency_code: string;
  reference: string | null;
  description: string;
  source_type: string | null;
  status: string;
};

type TreasuryLedgerRow = {
  id: string;
  treasury_account_id: string;
  account_code: string;
  account_name: string;
  movement_number: number | string;
  movement_type: string;
  movement_date: string;
  amount: number | string;
  signed_amount: number | string;
  currency_code: string;
  reference: string | null;
  description: string;
  source_type: string | null;
  source_id: string | null;
  accounting_entry_id: string | null;
  reversal_movement_id: string | null;
  status: string;
  reversal_reason: string | null;
  balance_after: number | string;
  total_rows: number | string;
};

type TreasurySummary = {
  accounts: TreasuryAccount[];
  receivables: Receivable[];
  payables: Payable[];
  recent_movements: TreasuryMovement[];
  totals: {
    cash_pen: number | string;
    receivable_pen: number | string;
    payable_pen: number | string;
  };
};

type AccountingCashAccount = {
  id: string;
  code: string;
  name: string;
};

type OpeningCounterpartAccount = {
  id: string;
  code: string;
  name: string;
  account_type: string;
};

type ManualCounterpartAccount = {
  id: string;
  code: string;
  name: string;
  account_type: string;
};

type SettlementAction = {
  kind: "ar" | "ap";
  id: string;
  label: string;
  currency: string;
  balance: number;
};

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function localDateTime() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60_000)
    .toISOString()
    .slice(0, 16);
}

function localDate() {
  return localDateTime().slice(0, 10);
}

export function TreasuryPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [summary, setSummary] = useState<TreasurySummary | null>(null);
  const [cashAccountingAccounts, setCashAccountingAccounts] = useState<
    AccountingCashAccount[]
  >([]);
  const [managedAccounts, setManagedAccounts] = useState<TreasuryAccountAdmin[]>(
    [],
  );
  const [openingCounterparts, setOpeningCounterparts] = useState<
    OpeningCounterpartAccount[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [panel, setPanel] = useState<
    "account" | "transfer" | "manual" | "ledger" | null
  >(null);
  const [settlement, setSettlement] = useState<SettlementAction | null>(null);

  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountCode, setAccountCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState<"cash" | "bank">("bank");
  const [accountCurrency, setAccountCurrency] = useState("PEN");
  const [accountingAccountId, setAccountingAccountId] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankNumber, setBankNumber] = useState("");
  const [bankAccountType, setBankAccountType] = useState("");
  const [cci, setCci] = useState("");
  const [allowNegativeBalance, setAllowNegativeBalance] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [accountNotes, setAccountNotes] = useState("");

  const [settlementAccountId, setSettlementAccountId] = useState("");
  const [settlementAmount, setSettlementAmount] = useState("");
  const [settlementMethod, setSettlementMethod] = useState("bank_transfer");
  const [settlementReference, setSettlementReference] = useState("");
  const [settlementDate, setSettlementDate] = useState(localDateTime());

  const [transferSource, setTransferSource] = useState("");
  const [transferDestination, setTransferDestination] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferReference, setTransferReference] = useState("");
  const [transferDate, setTransferDate] = useState(localDateTime());

  const [openingAccount, setOpeningAccount] =
    useState<TreasuryAccountAdmin | null>(null);
  const [openingAmount, setOpeningAmount] = useState("");
  const [openingCounterpartId, setOpeningCounterpartId] = useState("");
  const [openingDate, setOpeningDate] = useState(localDate());
  const [openingReason, setOpeningReason] = useState("");

  const [manualDirection, setManualDirection] =
    useState<"income" | "expense">("income");
  const [manualAccountId, setManualAccountId] = useState("");
  const [manualCounterparts, setManualCounterparts] = useState<
    ManualCounterpartAccount[]
  >([]);
  const [manualCounterpartId, setManualCounterpartId] = useState("");
  const [manualAmount, setManualAmount] = useState("");
  const [manualDescription, setManualDescription] = useState("");
  const [manualReference, setManualReference] = useState("");
  const [manualDate, setManualDate] = useState(localDateTime());

  const [reversalMovement, setReversalMovement] =
    useState<TreasuryMovement | null>(null);
  const [reversalReason, setReversalReason] = useState("");

  const [ledgerRows, setLedgerRows] = useState<TreasuryLedgerRow[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerAccountId, setLedgerAccountId] = useState("");
  const [ledgerDateFrom, setLedgerDateFrom] = useState("");
  const [ledgerDateTo, setLedgerDateTo] = useState("");
  const [ledgerType, setLedgerType] = useState("");
  const [ledgerStatus, setLedgerStatus] = useState("");

  const compatibleAccountingAccounts = useMemo(
    () =>
      cashAccountingAccounts.filter((account) =>
        accountType === "cash"
          ? account.code.startsWith("101")
          : account.code.startsWith("104"),
      ),
    [accountType, cashAccountingAccounts],
  );

  const hasCashAccountingAccount = useMemo(
    () => cashAccountingAccounts.some((account) => account.code.startsWith("101")),
    [cashAccountingAccounts],
  );

  const hasBankAccountingAccount = useMemo(
    () => cashAccountingAccounts.some((account) => account.code.startsWith("104")),
    [cashAccountingAccounts],
  );

  useEffect(() => {
    const selectedStillValid = compatibleAccountingAccounts.some(
      (account) => account.id === accountingAccountId,
    );

    if (selectedStillValid) return;

    setAccountingAccountId(
      compatibleAccountingAccounts.length === 1
        ? compatibleAccountingAccounts[0].id
        : "",
    );
  }, [compatibleAccountingAccounts, accountingAccountId]);

  const load = useCallback(async () => {
    if (!organizationId) {
      setSummary(null);
      setCashAccountingAccounts([]);
      setManagedAccounts([]);
      setOpeningCounterparts([]);
      return;
    }

    setLoading(true);
    setError(null);

    const [
      summaryResult,
      accountingResult,
      adminAccountsResult,
      openingCounterpartResult,
    ] = await Promise.all([
      supabase.rpc("get_organization_treasury_summary", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("list_organization_cash_accounting_accounts", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("list_organization_treasury_accounts_admin", {
        p_organization_id: organizationId,
      }),
      supabase.rpc("list_treasury_opening_counterpart_accounts", {
        p_organization_id: organizationId,
      }),
    ]);

    const firstError =
      summaryResult.error ??
      accountingResult.error ??
      adminAccountsResult.error ??
      openingCounterpartResult.error;

    if (firstError) {
      setError(firstError.message);
    }

    setSummary(
      summaryResult.error ? null : (summaryResult.data as TreasurySummary),
    );
    setCashAccountingAccounts(
      accountingResult.error
        ? []
        : ((accountingResult.data ?? []) as AccountingCashAccount[]),
    );
    setManagedAccounts(
      adminAccountsResult.error
        ? []
        : ((adminAccountsResult.data ?? []) as TreasuryAccountAdmin[]),
    );
    setOpeningCounterparts(
      openingCounterpartResult.error
        ? []
        : ((openingCounterpartResult.data ?? []) as OpeningCounterpartAccount[]),
    );

    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadLedger = useCallback(async () => {
    if (!organizationId) {
      setLedgerRows([]);
      return;
    }

    setLedgerLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc(
      "list_organization_treasury_ledger",
      {
        p_organization_id: organizationId,
        p_treasury_account_id: ledgerAccountId || null,
        p_date_from: ledgerDateFrom || null,
        p_date_to: ledgerDateTo || null,
        p_movement_type: ledgerType || null,
        p_status: ledgerStatus || null,
        p_limit: 1000,
        p_offset: 0,
      },
    );

    setLedgerLoading(false);

    if (rpcError) {
      setLedgerRows([]);
      setError(rpcError.message);
      return;
    }

    setLedgerRows((data ?? []) as TreasuryLedgerRow[]);
  }, [
    organizationId,
    ledgerAccountId,
    ledgerDateFrom,
    ledgerDateTo,
    ledgerType,
    ledgerStatus,
  ]);

  useEffect(() => {
    if (panel === "ledger") void loadLedger();
  }, [panel, loadLedger]);

  useEffect(() => {
    if (!organizationId || panel !== "manual") {
      setManualCounterparts([]);
      return;
    }

    let active = true;

    void supabase
      .rpc("list_manual_treasury_counterpart_accounts", {
        p_organization_id: organizationId,
        p_direction: manualDirection,
      })
      .then(({ data, error: rpcError }) => {
        if (!active) return;

        if (rpcError) {
          setManualCounterparts([]);
          setManualCounterpartId("");
          setError(rpcError.message);
          return;
        }

        const next = (data ?? []) as ManualCounterpartAccount[];
        setManualCounterparts(next);
        setManualCounterpartId(next.length === 1 ? next[0].id : "");
      });

    return () => {
      active = false;
    };
  }, [organizationId, panel, manualDirection]);

  const overdueReceivables = useMemo(
    () =>
      (summary?.receivables ?? []).filter(
        (item) =>
          item.due_date &&
          new Date(item.due_date + "T23:59:59").getTime() < Date.now(),
      ).length,
    [summary],
  );

  const overduePayables = useMemo(
    () =>
      (summary?.payables ?? []).filter(
        (item) =>
          item.due_date &&
          new Date(item.due_date + "T23:59:59").getTime() < Date.now(),
      ).length,
    [summary],
  );

  const settlementAccounts = useMemo(
    () =>
      (summary?.accounts ?? []).filter(
        (account) => account.currency_code === settlement?.currency,
      ),
    [summary, settlement],
  );

  const transferDestinations = useMemo(() => {
    const source = (summary?.accounts ?? []).find(
      (account) => account.id === transferSource,
    );

    if (!source) return [];

    return (summary?.accounts ?? []).filter(
      (account) =>
        account.id !== source.id &&
        account.currency_code === source.currency_code,
    );
  }, [summary, transferSource]);

  function openSettlement(action: SettlementAction) {
    const candidate = (summary?.accounts ?? []).find(
      (account) =>
        account.currency_code === action.currency &&
        (account.is_default || !settlementAccountId),
    );

    setSettlement(action);
    setSettlementAccountId(candidate?.id ?? "");
    setSettlementAmount(String(action.balance));
    setSettlementMethod("bank_transfer");
    setSettlementReference("");
    setSettlementDate(localDateTime());
    setError(null);
    setSuccess(null);
  }

  function resetAccountForm() {
    setEditingAccountId(null);
    setAccountCode("");
    setAccountName("");
    setAccountType("bank");
    setAccountCurrency("PEN");
    setAccountingAccountId("");
    setBankName("");
    setBankNumber("");
    setBankAccountType("");
    setCci("");
    setAllowNegativeBalance(false);
    setIsDefault(false);
    setAccountNotes("");
  }

  function startEditAccount(account: TreasuryAccountAdmin) {
    setEditingAccountId(account.id);
    setAccountCode(account.code);
    setAccountName(account.name);
    setAccountType(account.account_type);
    setAccountCurrency(account.currency_code);
    setAccountingAccountId(account.accounting_account_id);
    setBankName(account.bank_name ?? "");
    setBankNumber(account.bank_account_number ?? "");
    setBankAccountType(account.bank_account_type ?? "");
    setCci(account.cci ?? "");
    setAllowNegativeBalance(account.allow_negative_balance);
    setIsDefault(account.is_default);
    setAccountNotes(account.notes ?? "");
    setPanel("account");
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function exportLedgerCsv() {
    if (ledgerRows.length === 0) return;

    const escapeCsv = (value: unknown) => {
      const text = String(value ?? "");
      return `"${text.replace(/"/g, '""')}"`;
    };

    const header = [
      "numero",
      "fecha",
      "cuenta",
      "tipo",
      "estado",
      "moneda",
      "monto",
      "monto_firmado",
      "saldo",
      "referencia",
      "descripcion",
      "origen",
      "asiento_contable",
      "motivo_reversion",
    ];

    const rows = ledgerRows.map((row) => [
      row.movement_number,
      row.movement_date,
      `${row.account_code} · ${row.account_name}`,
      row.movement_type,
      row.status,
      row.currency_code,
      row.amount,
      row.signed_amount,
      row.balance_after,
      row.reference ?? "",
      row.description,
      row.source_type ?? "",
      row.accounting_entry_id ?? "",
      row.reversal_reason ?? "",
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsv).join(";"))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `libro-tesoreria-${localDate()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function resetManualMovement() {
    setManualDirection("income");
    setManualAccountId("");
    setManualCounterpartId("");
    setManualAmount("");
    setManualDescription("");
    setManualReference("");
    setManualDate(localDateTime());
  }

  function openManualMovement() {
    resetManualMovement();
    const candidate =
      (summary?.accounts ?? []).find((account) => account.is_default) ??
      (summary?.accounts ?? [])[0];

    setManualAccountId(candidate?.id ?? "");
    closeOpeningBalance();
    setSettlement(null);
    setPanel("manual");
    setError(null);
    setSuccess(null);
  }

  async function submitManualMovement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    const amount = Number(manualAmount);

    if (!manualAccountId || !manualCounterpartId) {
      setError("Selecciona la cuenta de Tesorería y la contrapartida contable.");
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Ingresa un monto válido.");
      return;
    }

    if (!manualDescription.trim()) {
      setError("Describe el motivo del movimiento.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "create_and_post_manual_treasury_movement",
      {
        p_organization_id: organizationId,
        p_treasury_account_id: manualAccountId,
        p_direction: manualDirection,
        p_amount: amount,
        p_counterpart_account_id: manualCounterpartId,
        p_description: manualDescription.trim(),
        p_reference: manualReference.trim() || null,
        p_movement_date: new Date(manualDate).toISOString(),
      },
    );

    setBusy(false);

    if (rpcError) {
      if (rpcError.message.includes("TREASURY_INSUFFICIENT_FUNDS")) {
        setError("La cuenta no tiene fondos suficientes para este egreso.");
      } else if (
        rpcError.message.includes("TREASURY_MOVEMENT_PERIOD_NOT_OPEN")
      ) {
        setError("La fecha elegida no pertenece a un periodo contable abierto.");
      } else if (
        rpcError.message.includes("TREASURY_MANUAL_COUNTERPART_CASH_BANK_FORBIDDEN")
      ) {
        setError(
          "Para mover fondos entre Caja/Banco utiliza la opción Transferir.",
        );
      } else {
        setError(rpcError.message);
      }
      return;
    }

    const label = manualDirection === "income" ? "Ingreso" : "Egreso";
    resetManualMovement();
    setPanel(null);
    setSuccess(`${label} manual registrado y contabilizado.`);
    await load();
  }

  function openMovementReversal(movement: TreasuryMovement) {
    setReversalMovement(movement);
    setReversalReason("");
    closeOpeningBalance();
    setSettlement(null);
    setPanel(null);
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  }

  async function submitMovementReversal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reversalMovement) return;

    const reason = reversalReason.trim();

    if (reason.length < 10) {
      setError("Indica un motivo de reversión de al menos 10 caracteres.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "reverse_manual_treasury_movement",
      {
        p_movement_id: reversalMovement.id,
        p_reason: reason,
      },
    );

    setBusy(false);

    if (rpcError) {
      if (
        rpcError.message.includes("TREASURY_REVERSAL_INSUFFICIENT_FUNDS")
      ) {
        setError(
          "No se puede revertir porque el saldo disponible no cubre la reversión.",
        );
      } else if (
        rpcError.message.includes("TREASURY_REVERSAL_PERIOD_NOT_OPEN")
      ) {
        setError("No existe un periodo contable abierto para la reversión.");
      } else if (
        rpcError.message.includes("TREASURY_REVERSAL_POSTED_REQUIRED") ||
        rpcError.message.includes("TREASURY_MOVEMENT_ALREADY_REVERSED")
      ) {
        setError("El movimiento ya no está disponible para reversión.");
      } else {
        setError(rpcError.message);
      }
      return;
    }

    setReversalMovement(null);
    setReversalReason("");
    setSuccess("Movimiento manual revertido y contraasiento contabilizado.");
    await load();
  }

  function openOpeningBalance(account: TreasuryAccountAdmin) {
    setOpeningAccount(account);
    setOpeningAmount("");
    setOpeningDate(localDate());
    setOpeningReason("");
    setOpeningCounterpartId(
      openingCounterparts.length === 1 ? openingCounterparts[0].id : "",
    );
    setPanel(null);
    setSettlement(null);
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function closeOpeningBalance() {
    setOpeningAccount(null);
    setOpeningAmount("");
    setOpeningCounterpartId("");
    setOpeningDate(localDate());
    setOpeningReason("");
  }

  async function submitOpeningBalance(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!openingAccount) return;

    const amount = Number(openingAmount);
    const reason = openingReason.trim();

    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Ingresa un saldo inicial válido.");
      return;
    }

    if (!openingCounterpartId) {
      setError("Selecciona la cuenta patrimonial de contrapartida.");
      return;
    }

    if (reason.length < 10) {
      setError("Indica un motivo de apertura de al menos 10 caracteres.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "set_treasury_opening_balance",
      {
        p_treasury_account_id: openingAccount.id,
        p_amount: amount,
        p_counterpart_account_id: openingCounterpartId,
        p_effective_date: openingDate,
        p_reason: reason,
      },
    );

    setBusy(false);

    if (rpcError) {
      if (rpcError.message.includes("TREASURY_OPENING_ALREADY_SET")) {
        setError("La cuenta ya tiene un saldo inicial registrado.");
      } else if (
        rpcError.message.includes("TREASURY_OPENING_REQUIRES_NO_MOVEMENTS")
      ) {
        setError(
          "El saldo inicial solo puede registrarse antes del primer movimiento.",
        );
      } else if (
        rpcError.message.includes("TREASURY_OPENING_PERIOD_NOT_OPEN")
      ) {
        setError("La fecha elegida no pertenece a un periodo contable abierto.");
      } else if (
        rpcError.message.includes("TREASURY_OPENING_COUNTERPART_INVALID")
      ) {
        setError("La cuenta patrimonial de contrapartida no es válida.");
      } else {
        setError(rpcError.message);
      }
      return;
    }

    const accountName = openingAccount.name;
    closeOpeningBalance();
    setSuccess(
      `Saldo inicial de ${accountName} registrado y contabilizado.`,
    );
    await load();
  }

  async function changeAccountStatus(account: TreasuryAccountAdmin) {
    setBusy(true);
    setError(null);
    setSuccess(null);

    const nextStatus = account.status === "active" ? "inactive" : "active";
    const { error: rpcError } = await supabase.rpc(
      "set_treasury_account_status",
      {
        p_treasury_account_id: account.id,
        p_status: nextStatus,
      },
    );

    setBusy(false);

    if (rpcError) {
      if (rpcError.message.includes("TREASURY_ACCOUNT_NONZERO_BALANCE")) {
        setError(
          "No se puede desactivar la cuenta porque su saldo no es cero.",
        );
      } else if (
        rpcError.message.includes("TREASURY_ACCOUNT_HAS_DRAFT_MOVEMENTS")
      ) {
        setError(
          "No se puede desactivar la cuenta porque tiene movimientos pendientes.",
        );
      } else {
        setError(rpcError.message);
      }
      return;
    }

    setSuccess(
      nextStatus === "active"
        ? "Cuenta de Tesorería activada."
        : "Cuenta de Tesorería desactivada.",
    );
    await load();
  }

  async function createAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    if (!accountingAccountId) {
      setError(
        accountType === "cash"
          ? "No existe una cuenta contable 101x disponible para Caja."
          : "No existe una cuenta contable 104x disponible para Banco.",
      );
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const payload = {
      p_code: accountCode,
      p_name: accountName,
      p_account_type: accountType,
      p_currency_code: accountCurrency,
      p_accounting_account_id: accountingAccountId,
      p_bank_name: accountType === "bank" ? bankName || null : null,
      p_bank_account_number:
        accountType === "bank" ? bankNumber || null : null,
      p_bank_account_type:
        accountType === "bank" ? bankAccountType || null : null,
      p_cci: accountType === "bank" ? cci || null : null,
      p_allow_negative_balance: allowNegativeBalance,
      p_is_default: isDefault,
      p_notes: accountNotes || null,
    };

    const { error: rpcError } = editingAccountId
      ? await supabase.rpc("update_organization_treasury_account", {
          p_treasury_account_id: editingAccountId,
          ...payload,
        })
      : await supabase.rpc("create_organization_treasury_account", {
          p_organization_id: organizationId,
          ...payload,
        });

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const wasEditing = Boolean(editingAccountId);
    resetAccountForm();
    setPanel(null);
    setSuccess(
      wasEditing
        ? "Cuenta de Tesorería actualizada."
        : "Cuenta de Tesorería creada.",
    );
    await load();
  }

  async function submitSettlement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!settlement) return;

    const amount = Number(settlementAmount);

    if (!settlementAccountId || !Number.isFinite(amount) || amount <= 0) {
      setError("Selecciona una cuenta e ingresa un monto válido.");
      return;
    }

    if (amount > settlement.balance) {
      setError("El monto supera el saldo pendiente.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const dateValue = new Date(settlementDate).toISOString();

    const result =
      settlement.kind === "ar"
        ? await supabase.rpc("register_ar_collection", {
            p_receivable_id: settlement.id,
            p_treasury_account_id: settlementAccountId,
            p_amount: amount,
            p_payment_method: settlementMethod,
            p_reference: settlementReference || null,
            p_notes: null,
            p_payment_date: dateValue,
          })
        : await supabase.rpc("register_ap_disbursement", {
            p_payable_id: settlement.id,
            p_treasury_account_id: settlementAccountId,
            p_amount: amount,
            p_payment_method: settlementMethod,
            p_reference: settlementReference || null,
            p_notes: null,
            p_payment_date: dateValue,
          });

    setBusy(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    setSuccess(
      settlement.kind === "ar"
        ? "Cobro registrado y contabilizado."
        : "Pago registrado y contabilizado.",
    );
    setSettlement(null);
    await load();
  }

  async function submitTransfer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    const amount = Number(transferAmount);

    if (
      !transferSource ||
      !transferDestination ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      setError("Completa las cuentas y el monto de la transferencia.");
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "create_and_post_treasury_transfer",
      {
        p_organization_id: organizationId,
        p_source_account_id: transferSource,
        p_destination_account_id: transferDestination,
        p_amount: amount,
        p_reference: transferReference || null,
        p_description: "Transferencia interna",
        p_transfer_date: new Date(transferDate).toISOString(),
      },
    );

    setBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setTransferSource("");
    setTransferDestination("");
    setTransferAmount("");
    setTransferReference("");
    setPanel(null);
    setSuccess("Transferencia registrada y contabilizada.");
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Tesorería</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Caja y bancos, cuentas por cobrar y cuentas por pagar.</p>
        </div>

        <div className="treasury-header-actions">
          <button
            className="ghost-button"
            type="button"
            onClick={() => {
              closeOpeningBalance();
              setSettlement(null);
              if (panel === "account") {
                setPanel(null);
                resetAccountForm();
              } else {
                resetAccountForm();
                setPanel("account");
              }
            }}
          >
            {panel === "account" ? "Cerrar" : "Nueva cuenta"}
          </button>
          <button
            className="ghost-button"
            type="button"
            disabled={(summary?.accounts.length ?? 0) < 2}
            onClick={() => {
              closeOpeningBalance();
              setSettlement(null);
              setPanel((current) =>
                current === "transfer" ? null : "transfer",
              );
            }}
          >
            Transferir
          </button>
          <button
            className="ghost-button"
            type="button"
            disabled={(summary?.accounts.length ?? 0) === 0}
            onClick={() => {
              if (panel === "manual") {
                resetManualMovement();
                setPanel(null);
              } else {
                openManualMovement();
              }
            }}
          >
            {panel === "manual" ? "Cerrar movimiento" : "Otro movimiento"}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => {
              closeOpeningBalance();
              setSettlement(null);
              setPanel((current) =>
                current === "ledger" ? null : "ledger",
              );
            }}
          >
            {panel === "ledger" ? "Cerrar libro" : "Libro"}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => void load()}
          >
            Actualizar
          </button>
        </div>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? (
        <div className="form-success treasury-message">{success}</div>
      ) : null}

      {panel === "ledger" ? (
        <section className="panel-card treasury-ledger-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Auditoría operativa</span>
              <h2>Libro de Tesorería</h2>
              <p>
                Movimientos de caja y bancos con saldo acumulado por cuenta.
              </p>
            </div>
            <div className="quotation-actions">
              <button
                className="compact-button"
                type="button"
                disabled={ledgerLoading}
                onClick={() => void loadLedger()}
              >
                {ledgerLoading ? "Consultando…" : "Aplicar filtros"}
              </button>
              <button
                className="compact-button"
                type="button"
                disabled={ledgerRows.length === 0}
                onClick={exportLedgerCsv}
              >
                Exportar CSV
              </button>
            </div>
          </div>

          <div className="treasury-ledger-filters">
            <label>
              Cuenta
              <select
                value={ledgerAccountId}
                onChange={(event) => setLedgerAccountId(event.target.value)}
              >
                <option value="">Todas</option>
                {managedAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name} · {account.currency_code}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Desde
              <input
                type="date"
                value={ledgerDateFrom}
                onChange={(event) => setLedgerDateFrom(event.target.value)}
              />
            </label>

            <label>
              Hasta
              <input
                type="date"
                value={ledgerDateTo}
                onChange={(event) => setLedgerDateTo(event.target.value)}
              />
            </label>

            <label>
              Tipo
              <select
                value={ledgerType}
                onChange={(event) => setLedgerType(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="income">Ingreso</option>
                <option value="expense">Egreso</option>
                <option value="transfer_in">Transferencia entrada</option>
                <option value="transfer_out">Transferencia salida</option>
                <option value="adjustment_in">Ajuste entrada</option>
                <option value="adjustment_out">Ajuste salida</option>
              </select>
            </label>

            <label>
              Estado
              <select
                value={ledgerStatus}
                onChange={(event) => setLedgerStatus(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="draft">Borrador</option>
                <option value="posted">Posteado</option>
                <option value="reversed">Revertido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </label>
          </div>

          <div className="treasury-ledger-meta">
            <span>
              {ledgerRows.length === 0
                ? "Sin movimientos para los filtros seleccionados."
                : `Mostrando ${ledgerRows.length} de ${Number(
                    ledgerRows[0]?.total_rows ?? ledgerRows.length,
                  )} movimiento(s).`}
            </span>
          </div>

          <div className="treasury-ledger-table-wrap">
            <table className="treasury-ledger-table">
              <thead>
                <tr>
                  <th>N.°</th>
                  <th>Fecha</th>
                  <th>Cuenta</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Descripción</th>
                  <th>Monto</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {ledgerRows.map((row) => (
                  <tr key={row.id}>
                    <td>#{row.movement_number}</td>
                    <td>
                      {new Date(row.movement_date).toLocaleString("es-PE")}
                    </td>
                    <td>
                      {row.account_code} · {row.account_name}
                    </td>
                    <td>{row.movement_type}</td>
                    <td>{row.status}</td>
                    <td>
                      <strong>{row.description}</strong>
                      {row.reference ? (
                        <small>{row.reference}</small>
                      ) : null}
                    </td>
                    <td>
                      <strong>
                        {money(row.signed_amount, row.currency_code)}
                      </strong>
                    </td>
                    <td>
                      {money(row.balance_after, row.currency_code)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="treasury-summary-grid">
        <div className="metric-card">
          <span>Disponible PEN</span>
          <strong>
            {loading ? "…" : money(summary?.totals.cash_pen ?? 0)}
          </strong>
        </div>
        <div className="metric-card">
          <span>Por cobrar PEN</span>
          <strong>
            {loading ? "…" : money(summary?.totals.receivable_pen ?? 0)}
          </strong>
        </div>
        <div className="metric-card">
          <span>Por pagar PEN</span>
          <strong>
            {loading ? "…" : money(summary?.totals.payable_pen ?? 0)}
          </strong>
        </div>
      </section>

      {panel === "account" ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                {editingAccountId ? "Edición" : "Configuración"}
              </span>
              <h2>
                {editingAccountId
                  ? "Editar cuenta de Tesorería"
                  : "Nueva cuenta de Tesorería"}
              </h2>
            </div>
          </div>

          <form className="treasury-form-grid" onSubmit={createAccount}>
            <label>
              Código
              <input
                required
                value={accountCode}
                onChange={(event) => setAccountCode(event.target.value)}
                placeholder="BCP-PEN"
              />
            </label>

            <label>
              Nombre
              <input
                required
                value={accountName}
                onChange={(event) => setAccountName(event.target.value)}
                placeholder="Cuenta corriente principal"
              />
            </label>

            <label>
              Tipo
              <select
                value={accountType}
                onChange={(event) =>
                  setAccountType(event.target.value as "cash" | "bank")
                }
              >
                <option value="bank" disabled={!hasBankAccountingAccount}>
                  Banco
                </option>
                <option value="cash" disabled={!hasCashAccountingAccount}>
                  Caja
                </option>
              </select>
            </label>

            <label>
              Moneda
              <select
                value={accountCurrency}
                onChange={(event) => setAccountCurrency(event.target.value)}
              >
                <option value="PEN">PEN</option>
                <option value="USD">USD</option>
              </select>
            </label>

            <label className="treasury-field-wide">
              Cuenta contable
              <select
                required
                value={accountingAccountId}
                onChange={(event) =>
                  setAccountingAccountId(event.target.value)
                }
              >
                <option value="">Seleccionar</option>
                {compatibleAccountingAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
              </select>
              {compatibleAccountingAccounts.length === 0 ? (
                <small className="settings-empty">
                  {accountType === "cash"
                    ? "Caja requiere una cuenta contable activa 101x."
                    : "Banco requiere una cuenta contable activa 104x."}
                </small>
              ) : null}
            </label>

            {accountType === "bank" ? (
              <>
                <label>
                  Banco
                  <input
                    value={bankName}
                    onChange={(event) => setBankName(event.target.value)}
                  />
                </label>
                <label>
                  N.° de cuenta
                  <input
                    value={bankNumber}
                    onChange={(event) => setBankNumber(event.target.value)}
                  />
                </label>
                <label>
                  Tipo de cuenta bancaria
                  <input
                    value={bankAccountType}
                    onChange={(event) =>
                      setBankAccountType(event.target.value)
                    }
                    placeholder="Corriente / Ahorros"
                  />
                </label>
                <label>
                  CCI
                  <input
                    value={cci}
                    onChange={(event) => setCci(event.target.value)}
                  />
                </label>
              </>
            ) : null}

            <label className="treasury-check">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(event) => setIsDefault(event.target.checked)}
              />
              Cuenta predeterminada
            </label>

            <label className="treasury-check">
              <input
                type="checkbox"
                checked={allowNegativeBalance}
                onChange={(event) =>
                  setAllowNegativeBalance(event.target.checked)
                }
              />
              Permitir saldo negativo
            </label>

            <label className="treasury-field-wide">
              Notas
              <input
                value={accountNotes}
                onChange={(event) => setAccountNotes(event.target.value)}
              />
            </label>

            <div className="treasury-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy
                  ? "Guardando…"
                  : editingAccountId
                    ? "Guardar cambios"
                    : "Crear cuenta"}
              </button>
              {editingAccountId ? (
                <button
                  className="ghost-button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    resetAccountForm();
                    setPanel(null);
                  }}
                >
                  Cancelar edición
                </button>
              ) : null}
            </div>
          </form>
        </section>
      ) : null}

      {panel === "manual" ? (
        <section className="panel-card treasury-manual-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Movimiento manual</span>
              <h2>
                {manualDirection === "income" ? "Otro ingreso" : "Otro egreso"}
              </h2>
            </div>
          </div>

          <div className="treasury-manual-note">
            Úsalo solo para movimientos que no provienen de Ventas/CxC,
            Compras/CxP ni transferencias internas. El registro genera
            automáticamente su asiento contable.
          </div>

          <form className="treasury-form-grid" onSubmit={submitManualMovement}>
            <label>
              Tipo
              <select
                value={manualDirection}
                onChange={(event) => {
                  setManualDirection(
                    event.target.value as "income" | "expense",
                  );
                  setManualCounterpartId("");
                }}
              >
                <option value="income">Otro ingreso</option>
                <option value="expense">Otro egreso</option>
              </select>
            </label>

            <label>
              Cuenta de Tesorería
              <select
                required
                value={manualAccountId}
                onChange={(event) => setManualAccountId(event.target.value)}
              >
                <option value="">Seleccionar</option>
                {(summary?.accounts ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {account.currency_code} ·{" "}
                    {money(account.current_balance, account.currency_code)}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Monto
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={manualAmount}
                onChange={(event) => setManualAmount(event.target.value)}
              />
            </label>

            <label>
              Fecha y hora
              <input
                required
                type="datetime-local"
                value={manualDate}
                onChange={(event) => setManualDate(event.target.value)}
              />
            </label>

            <label className="treasury-field-wide">
              Contrapartida contable
              <select
                required
                value={manualCounterpartId}
                onChange={(event) =>
                  setManualCounterpartId(event.target.value)
                }
              >
                <option value="">Seleccionar</option>
                {manualCounterparts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="treasury-field-wide">
              Descripción
              <input
                required
                value={manualDescription}
                onChange={(event) => setManualDescription(event.target.value)}
                placeholder="Ej.: Comisión bancaria, aporte, gasto no vinculado a CxP."
              />
            </label>

            <label className="treasury-field-wide">
              Referencia
              <input
                value={manualReference}
                onChange={(event) => setManualReference(event.target.value)}
              />
            </label>

            <div className="treasury-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy
                  ? "Procesando…"
                  : manualDirection === "income"
                    ? "Registrar ingreso"
                    : "Registrar egreso"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {panel === "transfer" ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Movimiento</span>
              <h2>Transferencia interna</h2>
            </div>
          </div>

          <form className="treasury-form-grid" onSubmit={submitTransfer}>
            <label>
              Cuenta origen
              <select
                required
                value={transferSource}
                onChange={(event) => {
                  setTransferSource(event.target.value);
                  setTransferDestination("");
                }}
              >
                <option value="">Seleccionar</option>
                {(summary?.accounts ?? []).map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {money(
                      account.current_balance,
                      account.currency_code,
                    )}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Cuenta destino
              <select
                required
                value={transferDestination}
                onChange={(event) =>
                  setTransferDestination(event.target.value)
                }
              >
                <option value="">Seleccionar</option>
                {transferDestinations.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Monto
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={transferAmount}
                onChange={(event) => setTransferAmount(event.target.value)}
              />
            </label>

            <label>
              Fecha y hora
              <input
                required
                type="datetime-local"
                value={transferDate}
                onChange={(event) => setTransferDate(event.target.value)}
              />
            </label>

            <label className="treasury-field-wide">
              Referencia
              <input
                value={transferReference}
                onChange={(event) =>
                  setTransferReference(event.target.value)
                }
              />
            </label>

            <div className="treasury-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? "Procesando…" : "Transferir"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {settlement ? (
        <section className="panel-card treasury-settlement-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                {settlement.kind === "ar" ? "Cobro" : "Pago"}
              </span>
              <h2>{settlement.label}</h2>
              <p>
                Saldo pendiente:{" "}
                {money(settlement.balance, settlement.currency)}
              </p>
            </div>
            <button
              className="ghost-button"
              type="button"
              onClick={() => setSettlement(null)}
            >
              Cerrar
            </button>
          </div>

          <form className="treasury-form-grid" onSubmit={submitSettlement}>
            <label>
              Cuenta
              <select
                required
                value={settlementAccountId}
                onChange={(event) =>
                  setSettlementAccountId(event.target.value)
                }
              >
                <option value="">Seleccionar</option>
                {settlementAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} · {money(
                      account.current_balance,
                      account.currency_code,
                    )}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Monto
              <input
                required
                type="number"
                min="0.01"
                max={settlement.balance}
                step="0.01"
                value={settlementAmount}
                onChange={(event) =>
                  setSettlementAmount(event.target.value)
                }
              />
            </label>

            <label>
              Medio
              <select
                value={settlementMethod}
                onChange={(event) =>
                  setSettlementMethod(event.target.value)
                }
              >
                <option value="bank_transfer">Transferencia bancaria</option>
                <option value="cash">Efectivo</option>
                <option value="card">Tarjeta</option>
                <option value="yape">Yape</option>
                <option value="plin">Plin</option>
                <option value="check">Cheque</option>
                <option value="other">Otro</option>
              </select>
            </label>

            <label>
              Fecha y hora
              <input
                required
                type="datetime-local"
                value={settlementDate}
                onChange={(event) =>
                  setSettlementDate(event.target.value)
                }
              />
            </label>

            <label className="treasury-field-wide">
              Referencia
              <input
                value={settlementReference}
                onChange={(event) =>
                  setSettlementReference(event.target.value)
                }
              />
            </label>

            <div className="treasury-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy
                  ? "Procesando…"
                  : settlement.kind === "ar"
                    ? "Registrar cobro"
                    : "Registrar pago"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {openingAccount ? (
        <section className="panel-card treasury-opening-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Apertura contable</span>
              <h2>Registrar saldo inicial</h2>
              <p>
                {openingAccount.name} · {openingAccount.currency_code}
              </p>
            </div>
            <button
              className="ghost-button"
              type="button"
              disabled={busy}
              onClick={closeOpeningBalance}
            >
              Cerrar
            </button>
          </div>

          <div className="treasury-opening-note">
            Este registro crea un asiento contable de apertura. No crea un
            movimiento operativo ni una línea bancaria artificial.
          </div>

          <form className="treasury-form-grid" onSubmit={submitOpeningBalance}>
            <label>
              Saldo inicial
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={openingAmount}
                onChange={(event) => setOpeningAmount(event.target.value)}
              />
            </label>

            <label>
              Fecha efectiva
              <input
                required
                type="date"
                value={openingDate}
                onChange={(event) => setOpeningDate(event.target.value)}
              />
            </label>

            <label className="treasury-field-wide">
              Contrapartida patrimonial
              <select
                required
                value={openingCounterpartId}
                onChange={(event) =>
                  setOpeningCounterpartId(event.target.value)
                }
              >
                <option value="">Seleccionar</option>
                {openingCounterparts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} · {account.name}
                  </option>
                ))}
              </select>
              {openingCounterparts.length === 0 ? (
                <small className="settings-empty">
                  No existe una cuenta patrimonial activa disponible para la
                  apertura.
                </small>
              ) : null}
            </label>

            <label className="treasury-field-wide">
              Motivo
              <textarea
                required
                minLength={10}
                rows={3}
                value={openingReason}
                onChange={(event) => setOpeningReason(event.target.value)}
                placeholder="Ej.: Saldo bancario existente al iniciar el uso del sistema."
              />
            </label>

            <div className="treasury-form-actions">
              <button
                className="primary-button"
                type="submit"
                disabled={busy || openingCounterparts.length === 0}
              >
                {busy ? "Registrando…" : "Registrar saldo inicial"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Caja y bancos</span>
            <h2>Cuentas de Tesorería</h2>
          </div>
        </div>

        <div className="treasury-account-grid">
          {managedAccounts.map((account) => (
            <article className="treasury-account-card" key={account.id}>
              <div>
                <div className="quotation-actions">
                  <span className="status-badge">{account.account_type}</span>
                  <span className="status-badge">{account.status}</span>
                  {account.is_default ? (
                    <span className="status-badge status-badge-approved">
                      predeterminada
                    </span>
                  ) : null}
                </div>
                <h3>{account.name}</h3>
                <p>
                  {account.code}
                  {account.bank_name ? " · " + account.bank_name : ""}
                </p>
                <small>
                  {account.accounting_account_code} ·{" "}
                  {account.accounting_account_name}
                </small>
                <div className="quotation-actions">
                  {account.status === "active" &&
                  Number(account.opening_balance || 0) === 0 &&
                  Number(account.current_balance || 0) === 0 ? (
                    <button
                      className="compact-button"
                      type="button"
                      disabled={busy}
                      onClick={() => openOpeningBalance(account)}
                    >
                      Saldo inicial
                    </button>
                  ) : null}
                  <button
                    className="compact-button"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      closeOpeningBalance();
                      startEditAccount(account);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    className="compact-button"
                    type="button"
                    disabled={busy}
                    onClick={() => void changeAccountStatus(account)}
                  >
                    {account.status === "active" ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </div>
              <strong>
                {money(account.current_balance, account.currency_code)}
              </strong>
            </article>
          ))}
        </div>
      </section>

      <div className="treasury-columns">
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">CxC</span>
              <h2>Cuentas por cobrar</h2>
              <p>{overdueReceivables} vencidas</p>
            </div>
          </div>

          <div className="treasury-list">
            {(summary?.receivables ?? []).map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.customer_name ?? "Cliente"}</strong>
                  <span>
                    {item.sale_number ?? item.document_number ?? "CxC"}
                    {" · "}
                    {item.due_date ?? "Sin vencimiento"}
                  </span>
                  {item.sale_id ? (
                    <Link
                      className="record-inline-link"
                      to={`/ventas/${item.sale_id}`}
                    >
                      Abrir venta
                    </Link>
                  ) : null}
                </div>
                <div className="treasury-item-actions">
                  <strong>
                    {money(item.balance_amount, item.currency_code)}
                  </strong>
                  <button
                    className="compact-button"
                    type="button"
                    disabled={
                      !(summary?.accounts ?? []).some(
                        (account) =>
                          account.currency_code === item.currency_code,
                      )
                    }
                    onClick={() =>
                      openSettlement({
                        kind: "ar",
                        id: item.id,
                        label:
                          item.sale_number ??
                          item.document_number ??
                          item.customer_name ??
                          "Cuenta por cobrar",
                        currency: item.currency_code,
                        balance: Number(item.balance_amount),
                      })
                    }
                  >
                    Cobrar
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">CxP</span>
              <h2>Cuentas por pagar</h2>
              <p>{overduePayables} vencidas</p>
            </div>
          </div>

          <div className="treasury-list">
            {(summary?.payables ?? []).map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.supplier_name ?? "Proveedor"}</strong>
                  <span>
                    {item.purchase_number ?? item.document_number ?? "CxP"}
                    {" · "}
                    {item.due_date ?? "Sin vencimiento"}
                  </span>
                  <div className="quotation-actions">
                    {item.purchase_order_id ? (
                      <Link
                        className="record-inline-link"
                        to={`/compras/${item.purchase_order_id}`}
                      >
                        Abrir compra
                      </Link>
                    ) : null}
                    {item.supplier_id ? (
                      <Link
                        className="record-inline-link"
                        to={`/proveedores/${item.supplier_id}`}
                      >
                        Abrir proveedor
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="treasury-item-actions">
                  <strong>
                    {money(item.balance_amount, item.currency_code)}
                  </strong>
                  <button
                    className="compact-button"
                    type="button"
                    disabled={
                      !(summary?.accounts ?? []).some(
                        (account) =>
                          account.currency_code === item.currency_code,
                      )
                    }
                    onClick={() =>
                      openSettlement({
                        kind: "ap",
                        id: item.id,
                        label:
                          item.purchase_number ??
                          item.document_number ??
                          item.supplier_name ??
                          "Cuenta por pagar",
                        currency: item.currency_code,
                        balance: Number(item.balance_amount),
                      })
                    }
                  >
                    Pagar
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      {reversalMovement ? (
        <section className="panel-card treasury-reversal-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Corrección contable</span>
              <h2>Revertir movimiento manual</h2>
              <p>
                {reversalMovement.description} ·{" "}
                {money(
                  reversalMovement.amount,
                  reversalMovement.currency_code,
                )}
              </p>
            </div>
            <button
              className="ghost-button"
              type="button"
              disabled={busy}
              onClick={() => {
                setReversalMovement(null);
                setReversalReason("");
              }}
            >
              Cerrar
            </button>
          </div>

          <div className="treasury-reversal-note">
            El movimiento original no se elimina. Se crea un movimiento
            compensatorio y un contraasiento, manteniendo toda la trazabilidad.
          </div>

          <form className="treasury-form-grid" onSubmit={submitMovementReversal}>
            <label className="treasury-field-wide">
              Motivo de reversión
              <textarea
                required
                minLength={10}
                rows={3}
                value={reversalReason}
                onChange={(event) => setReversalReason(event.target.value)}
                placeholder="Explica por qué debe revertirse este movimiento."
              />
            </label>

            <div className="treasury-form-actions">
              <button className="primary-button" type="submit" disabled={busy}>
                {busy ? "Revirtiendo…" : "Confirmar reversión"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historial</span>
            <h2>Movimientos recientes</h2>
          </div>
        </div>

        <div className="treasury-movement-list">
          {(summary?.recent_movements ?? []).map((movement) => (
            <article key={movement.id}>
              <div>
                <div className="quotation-actions">
                  <span className="status-badge">{movement.status}</span>
                  <span className="status-badge">{movement.movement_type}</span>
                </div>
                <strong>{movement.description}</strong>
                <span>
                  {movement.account_name} ·{" "}
                  {new Date(movement.movement_date).toLocaleString("es-PE")}
                </span>
                {movement.source_type === "manual_treasury" &&
                movement.status === "posted" ? (
                  <button
                    className="compact-button"
                    type="button"
                    disabled={busy}
                    onClick={() => openMovementReversal(movement)}
                  >
                    Revertir
                  </button>
                ) : null}
              </div>
              <div className="treasury-movement-amount">
                <strong>
                  {money(movement.amount, movement.currency_code)}
                </strong>
              </div>
            </article>
          ))}
        </div>
      </section>

      {!loading &&
      managedAccounts.length === 0 &&
      (summary?.receivables.length ?? 0) === 0 &&
      (summary?.payables.length ?? 0) === 0 ? (
        <section className="empty-state">
          <strong>Tesorería está lista para configurar.</strong>
          <p>
            {hasBankAccountingAccount
              ? "La configuración bancaria está disponible. Crea una cuenta para iniciar cobros y pagos."
              : "Primero configura una cuenta contable 104x para habilitar bancos."}
          </p>
          {!hasCashAccountingAccount ? (
            <p>
              Caja permanece bloqueada hasta disponer de una cuenta contable 101x.
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
